import React, { useState, useEffect } from 'react';
import { Edit2, RefreshCw, Eye, Check, X, Code, Globe, FileText } from 'lucide-react';
import { apiClient } from '../context/AuthContext';
import toast from 'react-hot-toast';
import SEOEditor from './SEOEditor';

export default function SEOPagesManager() {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingPage, setEditingPage] = useState(null);
  const [generatingSitemap, setGeneratingSitemap] = useState(false);
  const [sitemap, setSitemap] = useState('');
  const [showSitemap, setShowSitemap] = useState(false);

  useEffect(() => {
    fetchPages();
  }, []);

  const fetchPages = async () => {
    try {
      const { data } = await apiClient.get('/admin/seo-pages');
      setPages(data);
    } catch (error) {
      console.error('Error fetching SEO pages:', error);
      toast.error('Erreur lors du chargement des pages');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePage = async (pageId, updates) => {
    try {
      await apiClient.put(`/admin/seo-pages/${pageId}`, updates);
      toast.success('Page mise à jour !');
      fetchPages();
      setEditingPage(null);
    } catch (error) {
      console.error('Error updating page:', error);
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const toggleIndexable = async (page) => {
    try {
      await apiClient.put(`/admin/seo-pages/${page.page_id}`, {
        ...page,
        indexable: !page.indexable
      });
      toast.success(page.indexable ? 'Page non-indexée' : 'Page indexée');
      fetchPages();
    } catch (error) {
      console.error('Error toggling indexable:', error);
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const generateSitemap = async () => {
    setGeneratingSitemap(true);
    try {
      const { data } = await apiClient.post('/admin/sitemap/generate');
      setSitemap(data.sitemap);
      setShowSitemap(true);
      toast.success('Sitemap généré avec succès !');
    } catch (error) {
      console.error('Error generating sitemap:', error);
      toast.error('Erreur lors de la génération du sitemap');
    } finally {
      setGeneratingSitemap(false);
    }
  };

  const viewSitemap = async () => {
    try {
      const { data } = await apiClient.get('/admin/sitemap');
      setSitemap(data.sitemap);
      setShowSitemap(true);
    } catch (error) {
      console.error('Error viewing sitemap:', error);
      toast.error('Impossible de récupérer le sitemap.');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-[#00aaff] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Gestion SEO des Pages</h2>
          <p className="text-sm text-gray-500 mt-1">
            Configurez le référencement de chaque page de votre site
          </p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={viewSitemap}
            className="flex items-center gap-2 px-4 py-2.5 border border-gray-200 text-gray-700 rounded-xl font-semibold hover:bg-gray-50 transition-colors"
          >
            <Eye size={18} />
            Voir sitemap
          </button>
          <button
            onClick={generateSitemap}
            disabled={generatingSitemap}
            className="flex items-center gap-2 px-4 py-2.5 bg-[#00aaff] text-white rounded-xl font-semibold hover:bg-[#0088dd] transition-colors disabled:opacity-60"
          >
            {generatingSitemap ? (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            ) : (
              <RefreshCw size={18} />
            )}
            Générer sitemap.xml
          </button>
        </div>
      </div>

      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
        <p className="text-sm text-blue-700">
          <strong>💡 Conseil :</strong> Les pages non-indexées (noindex) ne seront pas référencées par Google. 
          Utilisez cette option pour les pages légales (CGV, mentions légales, politique de confidentialité).
        </p>
      </div>

      {/* Pages List */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase">Page</th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase">URL</th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase">Meta Title</th>
              <th className="px-6 py-4 text-center text-xs font-semibold text-gray-500 uppercase">Indexable</th>
              <th className="px-6 py-4 text-center text-xs font-semibold text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {pages.map((page) => (
              <tr key={page.page_id} className="hover:bg-gray-50 transition-colors">
                <td className="px-6 py-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#00aaff] to-[#0088dd] flex items-center justify-center">
                      <Globe size={20} className="text-white" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">{page.page_name}</p>
                      <p className="text-xs text-gray-500">{page.page_type || 'Statique'}</p>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <code className="text-sm text-gray-600 bg-gray-100 px-2 py-1 rounded">
                    {page.path}
                  </code>
                </td>
                <td className="px-6 py-4">
                  <p className="text-sm text-gray-700 truncate max-w-xs">
                    {page.meta_title || <span className="text-gray-400 italic">Non défini</span>}
                  </p>
                </td>
                <td className="px-6 py-4 text-center">
                  <button
                    onClick={() => toggleIndexable(page)}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                      page.indexable
                        ? 'bg-green-100 text-green-700 hover:bg-green-200'
                        : 'bg-red-100 text-red-700 hover:bg-red-200'
                    }`}
                  >
                    {page.indexable ? (
                      <>
                        <Check size={14} />
                        Oui
                      </>
                    ) : (
                      <>
                        <X size={14} />
                        Non
                      </>
                    )}
                  </button>
                </td>
                <td className="px-6 py-4 text-center">
                  <button
                    onClick={() => setEditingPage(page)}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium"
                  >
                    <Edit2 size={14} />
                    Modifier SEO
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Sitemap Viewer Modal */}
      {showSitemap && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b border-gray-100 flex justify-between items-center">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center">
                  <FileText size={20} className="text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Sitemap.xml</h2>
                  <p className="text-sm text-gray-500">Fichier généré automatiquement</p>
                </div>
              </div>
              <button onClick={() => setShowSitemap(false)} className="text-gray-400 hover:text-gray-600">
                <X size={20} />
              </button>
            </div>
            <div className="p-6 overflow-y-auto flex-1">
              <div className="bg-gray-900 rounded-xl p-4 overflow-x-auto">
                <pre className="text-sm text-green-400 font-mono">
                  <code>{sitemap}</code>
                </pre>
              </div>
              <div className="mt-4 bg-blue-50 border border-blue-200 rounded-xl p-4">
                <p className="text-sm text-blue-700">
                  <strong>📍 Emplacement :</strong> Le sitemap est accessible à l'URL : 
                  <code className="ml-2 bg-white px-2 py-1 rounded text-blue-800 font-mono">
                    https://joeycyan.com/sitemap.xml
                  </code>
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Page Modal */}
      {editingPage && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-100 flex justify-between items-center sticky top-0 bg-white z-10">
              <div>
                <h2 className="text-xl font-bold text-gray-900">
                  Configuration SEO : {editingPage.page_name}
                </h2>
                <p className="text-sm text-gray-500 mt-1">{editingPage.path}</p>
              </div>
              <button onClick={() => setEditingPage(null)} className="text-gray-400 hover:text-gray-600">
                <X size={20} />
              </button>
            </div>
            <div className="p-6">
              <SEOEditor
                metaTitle={editingPage.meta_title}
                metaDescription={editingPage.meta_description}
                slug={editingPage.slug}
                ogImage={editingPage.og_image}
                defaultTitle={editingPage.page_name}
                defaultDescription={editingPage.default_description || `Découvrez ${editingPage.page_name} sur Joey Cyan`}
                isProductContext={false}
                pagePath={editingPage.path}
                onChange={(seoData) => {
                  setEditingPage({
                    ...editingPage,
                    ...seoData
                  });
                }}
              />
            </div>
            <div className="p-6 border-t border-gray-100 flex gap-3">
              <button
                onClick={() => setEditingPage(null)}
                className="flex-1 border border-gray-200 text-gray-600 py-3 rounded-xl font-semibold hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                onClick={() => handleUpdatePage(editingPage.page_id, editingPage)}
                className="flex-1 bg-[#00aaff] text-white py-3 rounded-xl font-semibold hover:bg-[#0088dd]"
              >
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
